import React from 'react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Wrench, CheckCircle2, AlertTriangle, XCircle, Info, Clock, Gauge, TrendingDown } from 'lucide-react';
import { EngineModule } from '@/types/engineWizard';
import { calculateModuleHealth, formatHours, formatCycles, getStatusColor, getStatusBgColor } from '@/utils/engineCalculations';
import { ModuleLifeLimit, getModuleLifeLimit } from '@/data/moduleLifeLimits';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface EngineModuleHealthCardProps {
  module: EngineModule;
  manufacturer?: string;
  engineFamily?: string;
  compact?: boolean;
  className?: string;
}

export const EngineModuleHealthCard: React.FC<EngineModuleHealthCardProps> = ({
  module,
  manufacturer,
  engineFamily,
  compact = false,
  className,
}) => {
  const lifeLimit = manufacturer && engineFamily 
    ? getModuleLifeLimit(module.name, manufacturer, engineFamily)
    : undefined;

  const health = calculateModuleHealth(module, lifeLimit);

  const getConditionIcon = () => {
    switch (module.condition) {
      case 'new':
        return CheckCircle2;
      case 'overhauled':
        return CheckCircle2;
      case 'serviceable':
        return Wrench;
      case 'needs_repair':
        return XCircle;
      default:
        return AlertTriangle;
    }
  };

  const getConditionColor = () => {
    switch (module.condition) {
      case 'new':
        return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/20';
      case 'overhauled':
        return 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/20';
      case 'serviceable':
        return 'text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/20';
      case 'needs_repair':
        return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/20';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const ConditionIcon = getConditionIcon();

  if (compact) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={cn(
                'inline-flex items-center gap-3 rounded-xl border px-4 py-3 cursor-help transition-all duration-300 hover:scale-105 hover:shadow-lg',
                'bg-gradient-to-br',
                health.status === 'excellent' && 'from-green-500/10 to-green-600/5 border-green-500/30 hover:from-green-500/15',
                health.status === 'good' && 'from-blue-500/10 to-blue-600/5 border-blue-500/30 hover:from-blue-500/15',
                health.status === 'caution' && 'from-amber-500/10 to-amber-600/5 border-amber-500/30 hover:from-amber-500/15',
                (health.status === 'urgent' || health.status === 'overdue') && 'from-red-500/10 to-red-600/5 border-red-500/30 hover:from-red-500/15',
                className
              )}
            >
              <div className={cn(
                'w-10 h-10 rounded-full flex items-center justify-center',
                'bg-gradient-to-br shadow-inner',
                health.status === 'excellent' && 'from-green-500/20 to-green-600/10',
                health.status === 'good' && 'from-blue-500/20 to-blue-600/10',
                health.status === 'caution' && 'from-amber-500/20 to-amber-600/10',
                (health.status === 'urgent' || health.status === 'overdue') && 'from-red-500/20 to-red-600/10'
              )}>
                <ConditionIcon className={cn('h-5 w-5', getStatusColor(health.status))} />
              </div>
              <div className="flex flex-col min-w-0 overflow-hidden">
                <span className="text-sm font-semibold truncate">{module.name}</span>
                <span className={cn('text-lg font-bold leading-none', getStatusColor(health.status))}>
                  {health.overallHealth.toFixed(0)}%
                </span>
                <Badge variant="outline" className={cn('text-xs capitalize leading-none h-5 px-2 mt-1 w-fit', getStatusBgColor(health.status))}>
                  {health.status}
                </Badge>
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <div className="space-y-2">
              <p className="font-semibold text-base">{module.name}</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {module.tsn !== null && (
                  <div>
                    <span className="text-muted-foreground">TSN:</span>
                    <span className="ml-1 font-medium">{formatHours(module.tsn)} hrs</span>
                  </div>
                )}
                {module.tso !== null && (
                  <div>
                    <span className="text-muted-foreground">TSO:</span>
                    <span className="ml-1 font-medium">{formatHours(module.tso)} hrs</span>
                  </div>
                )}
                {module.csn !== null && (
                  <div>
                    <span className="text-muted-foreground">CSN:</span>
                    <span className="ml-1 font-medium">{formatCycles(module.csn)}</span>
                  </div>
                )}
                {module.cso !== null && (
                  <div>
                    <span className="text-muted-foreground">CSO:</span>
                    <span className="ml-1 font-medium">{formatCycles(module.cso)}</span>
                  </div>
                )}
              </div>
              {module.condition && (
                <p className="text-xs capitalize pt-1 border-t">
                  <span className="text-muted-foreground">Condition:</span> {module.condition.replace(/_/g, ' ')}
                </p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Card className={cn(
      'group relative overflow-hidden transition-all duration-500 hover:shadow-2xl border-2',
      health.status === 'excellent' && 'border-green-500/30 hover:border-green-500/50 shadow-green-500/10',
      health.status === 'good' && 'border-blue-500/30 hover:border-blue-500/50 shadow-blue-500/10',
      health.status === 'caution' && 'border-amber-500/30 hover:border-amber-500/50 shadow-amber-500/10',
      (health.status === 'urgent' || health.status === 'overdue') && 'border-red-500/30 hover:border-red-500/50 shadow-red-500/10',
      className
    )}>
      <div className={cn(
        'absolute inset-0 opacity-5 transition-opacity duration-500 group-hover:opacity-10',
        'bg-gradient-to-br',
        health.status === 'excellent' && 'from-green-400 via-green-500 to-green-600',
        health.status === 'good' && 'from-blue-400 via-blue-500 to-blue-600',
        health.status === 'caution' && 'from-amber-400 via-amber-500 to-amber-600',
        (health.status === 'urgent' || health.status === 'overdue') && 'from-red-400 via-red-500 to-red-600'
      )} />

      <CardContent className="relative z-10 p-5 space-y-4">
        {/* Header with Module Name and Health Score */}
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-2 flex-1 min-w-0">
            <h4 className="text-base font-bold flex items-center gap-2">
              <div className={cn(
                'w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0',
                'bg-gradient-to-br shadow-sm',
                health.status === 'excellent' && 'from-green-500/20 to-green-600/10',
                health.status === 'good' && 'from-blue-500/20 to-blue-600/10',
                health.status === 'caution' && 'from-amber-500/20 to-amber-600/10',
                (health.status === 'urgent' || health.status === 'overdue') && 'from-red-500/20 to-red-600/10'
              )}>
                <ConditionIcon className={cn('h-5 w-5', getStatusColor(health.status))} />
              </div>
              <span className="truncate">{module.name}</span>
            </h4>
            {module.condition && (
              <Badge variant="outline" className={cn('text-xs font-medium capitalize', getConditionColor())}>
                <ConditionIcon className="h-3 w-3 mr-1" />
                {module.condition.replace(/_/g, ' ')}
              </Badge>
            )}
          </div>

          {/* Radial Health Score with SVG Ring */}
          <div className="relative flex-shrink-0">
            <svg className="w-20 h-20 -rotate-90" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="8" opacity="0.1" />
              <circle
                cx="50" cy="50" r="40" fill="none" strokeWidth="8" strokeDasharray={251.2}
                strokeDashoffset={251.2 - (251.2 * health.overallHealth) / 100}
                strokeLinecap="round"
                className="transition-all duration-1000 ease-out"
                stroke={
                  health.status === 'excellent' ? 'rgb(34, 197, 94)' :
                  health.status === 'good' ? 'rgb(59, 130, 246)' :
                  health.status === 'caution' ? 'rgb(245, 158, 11)' : 'rgb(239, 68, 68)'
                }
                style={{ 
                  filter: `drop-shadow(0 0 8px ${
                    health.status === 'excellent' ? 'rgba(34, 197, 94, 0.5)' :
                    health.status === 'good' ? 'rgba(59, 130, 246, 0.5)' :
                    health.status === 'caution' ? 'rgba(245, 158, 11, 0.5)' : 'rgba(239, 68, 68, 0.5)'
                  })`
                }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={cn('text-xl font-bold leading-none', getStatusColor(health.status))}>
                {health.overallHealth.toFixed(0)}%
              </span>
              <span className="text-[10px] text-muted-foreground uppercase mt-0.5">Health</span>
            </div>
          </div>
        </div>

        {/* Enhanced Progress Bars */}
        <div className="space-y-3">
          {health.hoursRemaining !== null && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" />
                  Hours to Limit
                </span>
                {health.hoursRemaining === 0 && module.tso === 0 ? (
                  <Badge variant="secondary" className="text-xs bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20">New Module</Badge>
                ) : (
                  <span className={cn('font-bold', getStatusColor(health.status))}>{formatHours(health.hoursRemaining)} hrs</span>
                )}
              </div>
              {!(health.hoursRemaining === 0 && module.tso === 0) && (
                <div className="relative h-3 bg-black/10 dark:bg-black/20 rounded-full overflow-hidden border border-white/10 shadow-inner">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer" />
                  <div
                    className={cn('h-full transition-all duration-1000 ease-out relative bg-gradient-to-r',
                      health.status === 'excellent' && 'from-green-400 via-green-500 to-green-600',
                      health.status === 'good' && 'from-blue-400 via-blue-500 to-blue-600',
                      health.status === 'caution' && 'from-amber-400 via-amber-500 to-amber-600',
                      (health.status === 'urgent' || health.status === 'overdue') && 'from-red-400 via-red-500 to-red-600'
                    )}
                    style={{ 
                      width: `${health.overallHealth}%`,
                      boxShadow: `0 0 12px ${
                        health.status === 'excellent' ? 'rgba(34, 197, 94, 0.6)' :
                        health.status === 'good' ? 'rgba(59, 130, 246, 0.6)' :
                        health.status === 'caution' ? 'rgba(245, 158, 11, 0.6)' : 'rgba(239, 68, 68, 0.6)'
                      }`
                    }}
                  >
                    <div className="absolute inset-0 bg-white/20 animate-pulse" />
                  </div>
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-white drop-shadow-lg">
                    {health.overallHealth.toFixed(0)}%
                  </div>
                </div>
              )}
            </div>
          )}
          
          {health.cyclesRemaining !== null && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground flex items-center gap-1.5">
                  <Gauge className="w-3.5 h-3.5" />
                  Cycles to Limit
                </span>
                {health.cyclesRemaining === 0 && module.cso === 0 ? (
                  <Badge variant="secondary" className="text-xs bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20">New Module</Badge>
                ) : (
                  <span className={cn('font-bold', getStatusColor(health.status))}>{formatCycles(health.cyclesRemaining)} cycles</span>
                )}
              </div>
              {!(health.cyclesRemaining === 0 && module.cso === 0) && (
                <div className="relative h-3 bg-black/10 dark:bg-black/20 rounded-full overflow-hidden border border-white/10 shadow-inner">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer" />
                  <div
                    className={cn('h-full transition-all duration-1000 ease-out relative bg-gradient-to-r',
                      health.status === 'excellent' && 'from-green-400 via-green-500 to-green-600',
                      health.status === 'good' && 'from-blue-400 via-blue-500 to-blue-600',
                      health.status === 'caution' && 'from-amber-400 via-amber-500 to-amber-600',
                      (health.status === 'urgent' || health.status === 'overdue') && 'from-red-400 via-red-500 to-red-600'
                    )}
                    style={{ 
                      width: `${health.overallHealth}%`,
                      boxShadow: `0 0 12px ${
                        health.status === 'excellent' ? 'rgba(34, 197, 94, 0.6)' :
                        health.status === 'good' ? 'rgba(59, 130, 246, 0.6)' :
                        health.status === 'caution' ? 'rgba(245, 158, 11, 0.6)' : 'rgba(239, 68, 68, 0.6)'
                      }`
                    }}
                  >
                    <div className="absolute inset-0 bg-white/20 animate-pulse" />
                  </div>
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-white drop-shadow-lg">
                    {health.overallHealth.toFixed(0)}%
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border/50">
          {module.tsn !== null && (
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-gradient-to-br from-blue-500/5 to-blue-500/10 border border-blue-500/10">
              <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] text-muted-foreground uppercase">TSN</div>
                <div className="font-bold text-sm truncate">{formatHours(module.tsn)}</div>
              </div>
            </div>
          )}
          {module.tso !== null && (
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-gradient-to-br from-green-500/5 to-green-500/10 border border-green-500/10">
              <Clock className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] text-muted-foreground uppercase">TSO</div>
                <div className="font-bold text-sm truncate">{formatHours(module.tso)}</div>
              </div>
            </div>
          )}
          {module.csn !== null && (
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-gradient-to-br from-purple-500/5 to-purple-500/10 border border-purple-500/10">
              <Gauge className="w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] text-muted-foreground uppercase">CSN</div>
                <div className="font-bold text-sm truncate">{formatCycles(module.csn)}</div>
              </div>
            </div>
          )}
          {module.cso !== null && (
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-gradient-to-br from-amber-500/5 to-amber-500/10 border border-amber-500/10">
              <Gauge className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] text-muted-foreground uppercase">CSO</div>
                <div className="font-bold text-sm truncate">{formatCycles(module.cso)}</div>
              </div>
            </div>
          )}
        </div>

        {/* Notes */}
        {module.notes && (
          <div className="pt-3 border-t border-border/50">
            <p className="text-xs text-muted-foreground leading-relaxed">{module.notes}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
